import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { HOURLY_MAX_DAYS, REVENUE_BASES, type RevenueBasis, type RevenueGranularity } from '@/api/revenue'
import { dayCount, resolvePeriod, type GlobalFilters } from '@/lib/filters'
import { useUrlChoice } from '@/lib/useUrlChoice'

/*
 * The Revenue page's own URL vocabulary, kept beside the global filters so a link opens
 * the same view: the tab (`?tab=timing`), the basis (`?basis=net`), the trend's series
 * (`?metric=orders`, which the Overview's KPI tiles send) and its granularity.
 */

/** The tabs, in order (the Revenue Analytics proposal, screens 1–3). */
export const REVENUE_TABS = ['summary', 'competition', 'channel', 'customer', 'timing', 'leakage', 'orders'] as const
export type RevenueTab = (typeof REVENUE_TABS)[number]

export const REVENUE_TAB_LABELS: Record<RevenueTab, string> = {
  summary: 'Summary',
  competition: 'By competition',
  channel: 'By channel',
  customer: 'By customer',
  timing: 'Timing',
  leakage: 'Leakage',
  orders: 'Orders',
}

/** A breakdown tab's own dimension (`?by=`), which belongs to the tab it was chosen on. */
export const BY_PARAM = 'by'

/**
 * The open tab. Opening another drops the last tab's `?by=`, so a dimension chosen on one
 * breakdown tab never lingers in a link to the next.
 */
export function useRevenueTab(): [RevenueTab, (next: RevenueTab) => void] {
  const [params, setParams] = useSearchParams()
  const raw = params.get('tab')
  const tab = (REVENUE_TABS as readonly string[]).includes(raw ?? '') ? (raw as RevenueTab) : 'summary'
  const select = (next: RevenueTab) =>
    setParams(
      (current) => {
        const updated = new URLSearchParams(current)
        if (next === 'summary') updated.delete('tab')
        else updated.set('tab', next)
        if (next !== tab) updated.delete(BY_PARAM)
        return updated
      },
      { replace: true },
    )
  return [tab, select]
}

/** What the trend plots: the Overview's revenue, orders and AOV tiles each open it on their own. */
export const TREND_METRICS = ['revenue', 'orders', 'aov'] as const
export type TrendMetric = (typeof TREND_METRICS)[number]

export function useTrendMetric(): [TrendMetric, (next: TrendMetric) => void] {
  return useUrlChoice('metric', TREND_METRICS, 'revenue')
}

// --- the basis ----------------------------------------------------------------------------

export const BASIS_PARAM = 'basis'
/** This browser's last choice, in `localStorage`: a per-user preference, like the lists' columns. */
export const BASIS_STORAGE_KEY = 'ci.revenue.basis'
const DEFAULT_BASIS: RevenueBasis = 'gross'

function isBasis(value: string | null): value is RevenueBasis {
  return value !== null && (REVENUE_BASES as readonly string[]).includes(value)
}

/** The stored choice; storage can be missing or refused (a private window), and then there is none. */
function readStoredBasis(): RevenueBasis | null {
  try {
    const stored = window.localStorage.getItem(BASIS_STORAGE_KEY)
    return isBasis(stored) ? stored : null
  } catch {
    return null
  }
}

function storeBasis(basis: RevenueBasis) {
  try {
    window.localStorage.setItem(BASIS_STORAGE_KEY, basis)
  } catch {
    // Not kept: the choice still holds for this visit, in the URL.
  }
}

/**
 * The page-wide basis every query is read under. A `?basis=` in the link wins (so a link
 * can open the page in net), else this browser's last choice, else gross. Choosing one
 * remembers it and writes it to the URL — net as `?basis=net`, gross as no param — so a
 * copied link always opens what is on screen; a remembered net is written on arrival for
 * the same reason.
 */
export function useBasis(): [RevenueBasis, (next: RevenueBasis) => void] {
  const [params, setParams] = useSearchParams()
  const [stored, setStored] = useState(readStoredBasis)
  const raw = params.get(BASIS_PARAM)
  const basis = isBasis(raw) ? raw : (stored ?? DEFAULT_BASIS)
  // A remembered net not yet in the URL, or a `?basis=` the page does not know: either way
  // the URL is rewritten to say what is on screen.
  const unwritten = isBasis(raw) ? false : basis !== DEFAULT_BASIS || raw !== null

  const write = (next: RevenueBasis) =>
    setParams(
      (current) => {
        const updated = new URLSearchParams(current)
        if (next === DEFAULT_BASIS) updated.delete(BASIS_PARAM)
        else updated.set(BASIS_PARAM, next)
        return updated
      },
      { replace: true },
    )

  useEffect(() => {
    if (unwritten) write(basis)
    // `write` is a fresh closure each render over the same setter; the basis is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unwritten, basis])

  const choose = (next: RevenueBasis) => {
    storeBasis(next)
    setStored(next)
    write(next)
  }
  return [basis, choose]
}

// --- the trend's granularity -----------------------------------------------------------------

/** Ranges longer than this choose day, week or month; shorter ones read by the day. */
export const LONG_RANGE_DAYS = 60

/** What the range allows: the hour alone up to two days (the API's limit), the day, and week or month past 60 days. */
export function granularityOptions(days: number): readonly RevenueGranularity[] {
  if (days <= HOURLY_MAX_DAYS) return ['hour']
  return days > LONG_RANGE_DAYS ? ['day', 'week', 'month'] : ['day']
}

export const GRANULARITY_LABELS: Record<RevenueGranularity, string> = {
  hour: 'Hourly',
  day: 'Daily',
  week: 'Weekly',
  month: 'Monthly',
}

/**
 * The trend's granularity for a range of `days`: the first the range allows, or the one
 * chosen (`?granularity=`) when the range allows it, so a stale choice can never ask the
 * API for hours over a month.
 */
export function useTrendGranularity(days: number): [RevenueGranularity, readonly RevenueGranularity[], (next: RevenueGranularity) => void] {
  const options = granularityOptions(days)
  const fallback = options[0] ?? 'day'
  const [value, set] = useUrlChoice<RevenueGranularity>('granularity', options, fallback)
  return [value, options, set]
}

// --- the period ---------------------------------------------------------------------------------

export interface RevenuePeriod {
  /** The tenant days every route is asked for, both inclusive. */
  range: { from: string; to: string }
  days: number
}

/**
 * The period the panels size themselves by (the trend's granularity, the weekly panels'
 * weeks): the filters' own, which every route is sent as `from`/`to`, so it is the API's
 * range too. Never the last summary's: while new filters load that one is the previous
 * period's, and a trend sized by it would ask for hours over a week.
 */
export function usePeriod(filters: GlobalFilters): RevenuePeriod {
  const { current } = resolvePeriod(filters, new Date())
  return { range: { from: current.from, to: current.to }, days: dayCount(current.from, current.to) }
}
